import React from "react";
import { useTranslation } from "react-i18next";
import { MapPin } from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { whatsappUrl } from "../../config/contact";

import campecheImg from "../../assets/images/campeche_island.jpg";
import lagoinhaImg from "../../assets/images/lagoinha.webp";
import historicImg from "../../assets/images/historic-center.jpg";
import santoAntonioImg from "../../assets/images/santo-antonio.jpg";
import barraLagoaImg from "../../assets/images/barra-da-lagoa.jpg";
import betoCarreroImg from "../../assets/images/beto-carrero.jpg";

const tours = [
  { key: "campeche", image: campecheImg, color: "from-cyan-100 to-blue-100" },
  { key: "betoCarrero", image: betoCarreroImg, color: "from-blue-100 to-indigo-100" },
  { key: "lagoinha", image: lagoinhaImg, color: "from-emerald-100 to-teal-100" },
  { key: "historic", image: historicImg, color: "from-orange-100 to-amber-100" },
  { key: "santoAntonio", image: santoAntonioImg, color: "from-sky-100 to-cyan-100" },
  { key: "barraLagoa", image: barraLagoaImg, color: "from-blue-100 to-indigo-100" },
] as const;

export function Tours() {
  const { t } = useTranslation();

  return (
    <section id="tours" className="bg-slate-50 py-16 sm:py-24">
      <div className="container mx-auto px-4">
        <div className="mb-10 text-center sm:mb-16">
          <h2 className="mb-4 text-3xl font-bold text-slate-900 sm:text-4xl md:text-5xl">
            {t("tours.title")} <span className="text-primary">{t("tours.highlight")}</span>
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-slate-600">{t("tours.subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:gap-8 md:grid-cols-2 lg:grid-cols-3">
          {tours.map((tour) => {
            const title = t(`tours.items.${tour.key}.title`);
            return (
              <Card
                key={tour.key}
                className="group overflow-hidden border-slate-200 transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <div
                  className={`relative flex h-48 items-center justify-center overflow-hidden bg-gradient-to-br ${tour.color}`}
                >
                  {tour.image ? (
                    <img
                      src={tour.image}
                      alt={title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  ) : (
                    <MapPin className="h-12 w-12 text-black/10" />
                  )}
                </div>

                <CardHeader>
                  <CardTitle className="text-xl text-slate-900">{title}</CardTitle>
                  <CardDescription className="text-base text-slate-600">
                    {t(`tours.items.${tour.key}.description`)}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button className="w-full rounded-full bg-slate-900 font-semibold text-white hover:bg-slate-800" asChild>
                    <a
                      href={whatsappUrl(undefined, t("tours.whatsappText", { title }))}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("tours.learnMore")}
                    </a>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}
